import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { borderPrimary, cardBg, primaryText } from '../../../../styling/stylingHelper';
import type { SubrecipeImportDecision, SubrecipeImportPreview as ImportPreview, SubrecipeImportPreviewItem } from '../../../../services/subrecipes/subrecipesService';

export interface SubrecipeImportDraft {
  choice: string;
  name: string;
  portions: string;
  portionsUnit: 'g' | 'ml' | 'p';
}

export function chooseSubrecipeImport(item: SubrecipeImportPreviewItem, choice: string): SubrecipeImportDraft {
  const target = item.candidates.find(candidate => choice === `update:${candidate.id}`);
  const unit = target?.portionsUnit ?? item.portionsUnit;
  // The incoming file's yield wins, also on filled recipes, when it fits the unit dishes rely on.
  const portions = !target ? String(item.portions)
    : unit === item.portionsUnit && !item.portionsWasGuessed ? String(item.portions)
      : unit === 'p' && target.ingredientCount > 0 ? String(target.portions) : '';
  return { choice, name: item.name, portions, portionsUnit: unit as SubrecipeImportDraft['portionsUnit'] };
}

export function initialSubrecipeImportDrafts(preview: ImportPreview): SubrecipeImportDraft[] {
  return preview.items.map(item => {
    const exact = item.candidates.filter(candidate => candidate.exact);
    const exactTarget = exact.length === 1 && !!exact[0].expectedUpdatedAt;
    return chooseSubrecipeImport(item, item.duplicateOf !== undefined ? 'skip'
      : exactTarget ? `update:${exact[0].id}` : item.candidates.length ? '' : 'create');
  });
}

/** Mirror the server's validation so conflicts are actionable before submitting. */
export function subrecipeImportDecisions(preview: ImportPreview, drafts: SubrecipeImportDraft[]) {
  const decisions: SubrecipeImportDecision[] = [];
  const issues: Record<number, string> = {};
  const names = new Set(preview.existingNames.map(name => name.trim().toLowerCase()));
  const targets = new Set<string>();
  for (const item of preview.items) {
    const draft = drafts[item.index];
    if (!draft?.choice) { issues[item.index] = 'chooseAction'; continue; }
    if (draft.choice === 'skip') { decisions.push({ index: item.index, action: 'skip' }); continue; }
    const portions = Number(draft.portions);
    if (!Number.isFinite(portions) || portions <= 0) { issues[item.index] = 'enterYield'; continue; }
    if (draft.choice === 'create') {
      const name = draft.name.trim();
      if (!name) { issues[item.index] = 'enterName'; continue; }
      if (names.has(name.toLowerCase())) { issues[item.index] = 'duplicateName'; continue; }
      names.add(name.toLowerCase());
      decisions.push({ index: item.index, action: 'create', name, portions, portionsUnit: draft.portionsUnit });
    } else {
      const target = item.candidates.find(candidate => draft.choice === `update:${candidate.id}`);
      if (!target?.expectedUpdatedAt || !['g', 'ml', 'p'].includes(target.portionsUnit)) { issues[item.index] = 'unavailableTarget'; continue; }
      if (targets.has(target.id)) { issues[item.index] = 'duplicateTarget'; continue; }
      targets.add(target.id);
      decisions.push({ index: item.index, action: 'update', targetId: target.id, expectedUpdatedAt: target.expectedUpdatedAt,
        portions, portionsUnit: target.portionsUnit as 'g' | 'ml' | 'p' });
    }
  }
  return { decisions, issues, valid: Object.keys(issues).length === 0 };
}

interface Props {
  preview: ImportPreview;
  drafts: SubrecipeImportDraft[];
  onChange: (drafts: SubrecipeImportDraft[]) => void;
  disabled: boolean;
}

export default function SubrecipeImportPreview({ preview, drafts, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(20);
  const key = (name: string) => `costPrice.uploadSubrecipeTemplate.matchReview.${name}`;
  const exact = preview.items.filter(item => item.candidates.some(candidate => candidate.exact)).length;
  const close = preview.items.filter(item => item.candidates.length && !item.candidates.some(candidate => candidate.exact)).length;
  const { issues } = subrecipeImportDecisions(preview, drafts);
  const inputClass = `w-full min-w-0 rounded-md border p-2 ${borderPrimary} ${cardBg} ${primaryText}`;
  const patch = (index: number, draft: SubrecipeImportDraft) => onChange(drafts.map((current, i) => i === index ? draft : current));

  return (
    <section className={`space-y-4 ${primaryText}`} aria-label={t(key('title'))}>
      <h3 className="font-semibold">{t(key('title'))}</h3>
      <p className="text-sm">{t(key('summary'), { exact, close, new: preview.items.length - exact - close })}</p>
      <p className="text-sm opacity-70">{t(key('hint'))}</p>
      {preview.items.slice(0, visibleCount).map(item => {
        const draft = drafts[item.index];
        const target = item.candidates.find(candidate => draft.choice === `update:${candidate.id}`);
        const labelId = `subrecipe-import-${item.index}`;
        return (
          <fieldset key={item.index} disabled={disabled} className={`min-w-0 rounded-lg border p-3 space-y-3 ${borderPrimary}`}>
            <legend className="px-1 font-medium break-words">{item.name}</legend>
            <p className="text-sm opacity-70">{t(key('ingredientCount'), { count: item.ingredientCount })}</p>
            {item.duplicateOf !== undefined && <p className="text-sm">{t(key('duplicateInFile'))}</p>}
            <label htmlFor={`${labelId}-action`} className="block text-sm">{t(key('action'))}</label>
            <select id={`${labelId}-action`} className={inputClass} value={draft.choice}
              onChange={event => patch(item.index, chooseSubrecipeImport(item, event.target.value))}>
              <option value="">{t(key('chooseAction'))}</option>
              {item.candidates.map(candidate => (
                <option key={candidate.id} value={`update:${candidate.id}`} disabled={!candidate.expectedUpdatedAt || !['g', 'ml', 'p'].includes(candidate.portionsUnit)}>
                  {t(key(candidate.ingredientCount === 0 ? 'fillCandidate' : 'addToCandidate'), {
                    name: candidate.name, match: t(key(candidate.exact ? 'exact' : 'closeMatch')),
                  })}
                </option>
              ))}
              <option value="create">{t(key('create'))}</option>
              <option value="skip">{t(key('skip'))}</option>
            </select>
            {draft.choice === 'create' && (
              <label className="block text-sm">{t(key('newName'))}
                <input className={`${inputClass} mt-1`} value={draft.name}
                  onChange={event => patch(item.index, { ...draft, name: event.target.value })} />
              </label>
            )}
            {target && <p className="text-sm">{t(key(target.ingredientCount ? 'addHint' : 'fillHint'), { name: target.name, count: target.ingredientCount })}</p>}
            {draft.choice && draft.choice !== 'skip' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block text-sm">{t(key('yield'))}
                    <input type="number" min="0" step="any" className={`${inputClass} mt-1`} value={draft.portions}
                      onChange={event => patch(item.index, { ...draft, portions: event.target.value })} />
                  </label>
                  <label className="block text-sm">{t(key('unit'))}
                    <select className={`${inputClass} mt-1`} value={draft.portionsUnit} disabled={!!target}
                      onChange={event => patch(item.index, { ...draft, portionsUnit: event.target.value as 'g' | 'ml' | 'p' })}>
                      {(['g', 'ml', 'p'] as const).map(unit => <option key={unit} value={unit}>{t(key(`units.${unit}`))}</option>)}
                    </select>
                  </label>
                </div>
                <p className="text-xs opacity-70">{t(key(target ? 'existingUnitHint' : 'estimatedYieldHint'))}</p>
              </>
            )}
            {issues[item.index] && <p className="text-sm text-amber-700 dark:text-amber-300">{t(key(issues[item.index]))}</p>}
          </fieldset>
        );
      })}
      {visibleCount < preview.items.length && (
        <button type="button" disabled={disabled} className={`w-full rounded-md border p-2 ${borderPrimary}`}
          onClick={() => setVisibleCount(count => count + 20)}>{t(key('showMore'), { count: preview.items.length - visibleCount })}</button>
      )}
    </section>
  );
}
